import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Users, Ban, UserX, Smartphone, StickyNote, MoreHorizontal } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useFlash } from "../lib/flash";
import { fmtShortDate, formatKr } from "../lib/format";
import { Pill } from "../components/bits";
import { KpiCard, KpiSkeleton } from "../components/KpiStrip";
import { rateTone } from "../components/OfferRevenue";
import CustomerPanel from "../components/CustomerPanel";

const Kr = ({ ore }) => <span className={ore ? "" : "muted"}>{ore ? formatKr(ore) : "0 kr."}</span>;
const Rate = ({ r }) => (r == null ? <span className="muted">—</span> : <b className={`orv-rate ${rateTone(r)}`}>{r} %</b>);

function Actions({ c, act }) {
  const [open, setOpen] = useState(false);
  const btns = (
    <>
      {c.no_show_count ? (
        <button className="btn small" onClick={() => act(c.id, "reset")} data-testid={`customer-reset-${c.id}`}>Nulstil</button>
      ) : null}
      {c.blocked ? (
        <button className="btn small" onClick={() => act(c.id, "unblock")} data-testid={`customer-unblock-${c.id}`}>Lås op</button>
      ) : (
        <button className="btn small cu-block" onClick={() => act(c.id, "block", c.name)} data-testid={`customer-block-${c.id}`}>Bloker</button>
      )}
    </>
  );
  return (
    <div className="cu-acts">
      <div className="cu-acts-inline">{btns}</div>
      <div className="cu-acts-menu">
        <button className="ck-close" aria-label="Flere handlinger" aria-expanded={open} onClick={() => setOpen((o) => !o)} data-testid={`customer-menu-${c.id}`}>
          <MoreHorizontal size={18} />
        </button>
        {open && <div className="cu-menu" onClick={() => setOpen(false)}>{btns}</div>}
      </div>
    </div>
  );
}

function Name({ c, onOpen }) {
  return (
    <span className="cu-name">
      <button type="button" className="pc-name" onClick={() => onOpen(c.id)} data-testid={`customer-name-${c.id}`}>{c.name}</button>
      {c.name_source === "customer" && <span className="by-customer">(skrevet af kunden)</span>}
      {c.open_orders ? <small className="muted"> ({c.open_orders} åben)</small> : null}
      {c.note && (
        <span className="cu-note" title={c.note} data-testid={`customer-note-${c.id}`}>
          <StickyNote size={13} /> {c.note.length > 40 ? c.note.slice(0, 40) + "…" : c.note}
        </span>
      )}
    </span>
  );
}

function Row({ c, act, onOpen }) {
  return (
    <tr data-testid={`customer-row-${c.id}`}>
      <td><Name c={c} onOpen={onOpen} /></td>
      <td>{c.phone ? "+" + c.phone : <span className="muted">—</span>}</td>
      <td className="num" data-testid={`customer-orders-${c.id}`}>{c.orders_count}</td>
      <td className="num">{c.pickups_count}</td>
      <td className="num">{c.no_show_count}</td>
      <td className="num" data-testid={`customer-revenue-${c.id}`}><Kr ore={c.revenue_ore} /></td>
      <td className="num" data-testid={`customer-rate-${c.id}`}><Rate r={c.pickup_rate} /></td>
      <td><Pill status={c.status_cls} label={c.status_label} /></td>
      <td><small>{fmtShortDate(c.last_order)}</small></td>
      <td className="num"><Actions c={c} act={act} /></td>
    </tr>
  );
}

function Card({ c, act, onOpen }) {
  const stats = [["Ordrer", c.orders_count], ["Hentet", c.pickups_count], ["Udeblevet", c.no_show_count],
    ["Køb i alt", <Kr ore={c.revenue_ore} />], ["Afhentning", <Rate r={c.pickup_rate} />], ["Seneste", fmtShortDate(c.last_order) || "—"]];
  return (
    <div className="cu-card" data-testid={`customer-card-${c.id}`}>
      <div className="cu-card-top">
        <Name c={c} onOpen={onOpen} />
        <Pill status={c.status_cls} label={c.status_label} />
      </div>
      {c.phone && <a className="muted cu-phone" href={`tel:+${c.phone}`}>+{c.phone}</a>}
      <div className="cu-grid">
        {stats.map(([l, v]) => <div key={l}><small>{l}</small><b>{v}</b></div>)}
      </div>
      <Actions c={c} act={act} />
    </div>
  );
}

function Kpis({ k, flagged, setFlagged }) {
  return (
    <section className="kstrip cu-kpis" data-testid="customer-kpis">
      <KpiCard icon={Users} value={k.total} label="kunder i alt" testid="ckpi-total" onClick={() => setFlagged(false)} />
      <KpiCard icon={Ban} tone={k.blocked ? "red" : "green"} value={k.blocked} label="blokerede" testid="ckpi-blocked" onClick={() => setFlagged(true)} />
      <KpiCard icon={UserX} tone="amber" value={k.with_noshows} label={flagged ? "med udeblivelser · vist" : "med udeblivelser"} testid="ckpi-noshows" onClick={() => setFlagged((f) => !f)} />
      <KpiCard icon={Smartphone} value={k.with_phone} label="med mobilnummer" testid="ckpi-phone" onClick={() => setFlagged(false)} />
    </section>
  );
}

export default function Customers() {
  const flash = useFlash();
  const [data, setData] = useState(null);
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [flagged, setFlagged] = useState(false);
  const [panel, setPanel] = useState(null);

  const load = useCallback(() => {
    api.get("/customers", { params: { q, filter: flagged ? "flagged" : "" } }).then((r) => setData(r.data));
  }, [q, flagged]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const act = async (cid, action, name) => {
    if (action === "block" && !window.confirm(`Bloker ${name}? De kan ikke reservere, før du låser op.`)) return;
    try {
      const r = await api.post(`/customers/${cid}/${action}`);
      if (r.data.flash) flash(r.data.flash);
      load();
    } catch (err) {
      flash(apiError(err.response?.data?.detail) || err.message, "error");
    }
  };

  const s = data?.settings;
  const list = data?.customers || [];
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Kunder</h1>
          {s && (
            <p>
              {s.noshow_confirm_enabled === "1" && Number(s.noshow_confirm_threshold) > 0
                ? `Kunder med ${s.noshow_confirm_threshold}+ udeblivelser skal bekræfte deres reservation. `
                : ""}
              Ved {s.noshow_block_threshold}+ udeblivelser blokeres de. Hver afhentning sletter én udeblivelse.
            </p>
          )}
        </div>
        <div className="actions">
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søg navn eller nummer" style={{ width: 220 }} data-testid="customer-search" />
          <label className="check" style={{ margin: 0 }}>
            <input type="checkbox" checked={flagged} onChange={(e) => setFlagged(e.target.checked)} data-testid="customer-flagged" /> Kun med udeblivelser
          </label>
        </div>
      </div>
      {data ? <Kpis k={data.kpis} flagged={flagged} setFlagged={setFlagged} /> : <KpiSkeleton />}
      <section className="panel cu-panel">
        {!data ? (
          <div className="cp-loading"><span className="sk sk-line" /><span className="sk sk-line" /><span className="sk sk-line" /></div>
        ) : (
          <>
            <table className="cu-table" data-testid="customer-table">
              <thead>
                <tr>
                  <th>Navn</th><th>Mobil</th><th className="num">Ordrer</th><th className="num">Hentet</th>
                  <th className="num">Udeblevet</th><th className="num">Køb i alt</th><th className="num">Afhentning</th>
                  <th>Status</th><th>Seneste</th><th></th>
                </tr>
              </thead>
              <tbody>
                {list.length ? list.map((c) => <Row key={c.id} c={c} act={act} onOpen={setPanel} />)
                  : <tr><td colSpan={10} className="empty">Ingen kunder endnu</td></tr>}
              </tbody>
            </table>
            <div className="cu-cards">
              {list.length ? list.map((c) => <Card key={c.id} c={c} act={act} onOpen={setPanel} />) : <p className="empty">Ingen kunder endnu</p>}
            </div>
          </>
        )}
      </section>
      {panel && <CustomerPanel cid={panel} onClose={() => setPanel(null)} onChanged={load} hideProfile />}
    </>
  );
}
